import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { buildKeywords } from "@/components/KeywordsSuggestBlock";
import { SuggestLink } from "@/components/SuggestLink";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { formatDateRange } from "@/components/EventCard";
import { CalendarDays, MapPin, ArrowLeft, Share2, MessageCircle, ExternalLink, Navigation2, Facebook, Copy } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import FbGroupShare from "@/components/FbGroupShare";
import FeaturedPartners from "@/components/FeaturedPartners";
import EventBrandMentions from "@/components/EventBrandMentions";

export default function EventDetail() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);

  useEffect(() => {
    api.get(`/public/events/${slug}`).then((r) => setEvent(r.data)).catch(() => setEvent(false));
  }, [slug]);

  const _ev = (event && typeof event === "object") ? event : null;
  useSeo({
    title: _ev ? `${_ev.title} — ${_ev.city} | PROXIreduc-28` : undefined,
    description: _ev ? ((_ev.description || "").slice(0, 200) || `${_ev.title} à ${_ev.city}`) : undefined,
    canonicalPath: _ev ? `/sortir/${_ev.slug || slug}` : undefined,
    image: _ev ? (_ev.image || "/og-sortir.jpg") : "/og-sortir.jpg",
    keywords: _ev ? buildKeywords(_ev.category_label, _ev.city).join(", ") : undefined,
    type: "article",
  });

  if (event === null) return <div className="p-10 text-center text-slate-500" data-testid="event-loading">Chargement…</div>;
  if (event === false) return <Navigate to="/sortir" replace/>;

  const ogUrl = `${process.env.REACT_APP_BACKEND_URL}/api/event-vitrine/${event.slug || slug}`;
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: event.title, text: event.title, url: ogUrl }); return; } catch { /* fallback */ }
    }
    try { await navigator.clipboard.writeText(ogUrl); toast.success("Lien copié !"); }
    catch { toast.info(ogUrl); }
  };
  const whatsapp = () => {
    const msg = `À ne pas manquer sur PROXIreduc-28 : ${event.title} — ${event.city}\n${ogUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };
  const facebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogUrl)}`, "_blank", "noopener,noreferrer");
  };
  const copyFull = async () => {
    const txt = `${event.title}${event.city ? ` — ${event.city}` : ""}\n\n${event.description || "Détails à venir."}\n\nPlus d'infos et partage : ${ogUrl}`;
    try { await navigator.clipboard.writeText(txt); toast.success("Descriptif copié ! Collez-le où vous voulez."); }
    catch { toast.info("Copie impossible sur cet appareil."); }
  };
  const itin = event.address || event.city
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${event.address || ""} ${event.city || ""}`.trim())}`
    : null;

  return (
    <div data-testid="event-detail">
      <div className="relative h-56 md:h-80 bg-slate-200 overflow-hidden">
        {event.image ? <img src={event.image} alt={event.title} className="w-full h-full object-cover"/> :
          <div className="w-full h-full grid place-items-center text-slate-400"><CalendarDays size={60}/></div>}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent"></div>
        <div className="absolute bottom-6 left-0 right-0 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <Link to="/sortir" className="inline-flex items-center gap-1 text-xs text-white/80 hover:text-white"><ArrowLeft size={14}/> Retour à l'agenda</Link>
          <div className="mt-2 text-xs uppercase font-bold tracking-widest text-amber-300">{event.category_label}</div>
          <h1 className="mt-1 font-heading text-3xl md:text-5xl font-extrabold" data-testid="event-title">{event.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-1"><CalendarDays size={14}/> {formatDateRange(event.start_date, event.end_date)}</span>
            <span className="inline-flex items-center gap-1"><MapPin size={14}/> {event.city}</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-6">
          <div className="flex flex-wrap gap-2" data-testid="event-actions">
            {itin && (
              <a href={itin} target="_blank" rel="noreferrer">
                <Button variant="outline" className="h-10"><Navigation2 size={14} className="mr-1.5"/> Itinéraire</Button>
              </a>
            )}
            <Button onClick={facebook} variant="outline" className="h-10 text-[#1877F2] border-blue-200 hover:bg-blue-50" data-testid="event-facebook">
              <Facebook size={14} className="mr-1.5"/> Facebook
            </Button>
            <Button onClick={whatsapp} variant="outline" className="h-10 text-green-700 border-green-300 hover:bg-green-50" data-testid="event-whatsapp">
              <MessageCircle size={14} className="mr-1.5"/> WhatsApp
            </Button>
            <Button onClick={share} variant="outline" className="h-10" data-testid="event-share"><Share2 size={14} className="mr-1.5"/> Partager</Button>
            <Button onClick={copyFull} variant="outline" className="h-10" data-testid="event-copy"><Copy size={14} className="mr-1.5"/> Copier</Button>
          </div>

          {(event.source_event_url || event.source_site) && (
            <a href={event.source_event_url || event.source_site} target="_blank" rel="noreferrer"
               data-testid="event-source-cta"
               className="flex items-center justify-between gap-4 rounded-2xl border-2 border-[#E05638]/30 bg-rose-50 px-5 py-4 hover:bg-rose-100 transition-colors group">
              <div className="min-w-0">
                <div className="font-heading font-bold text-slate-900">
                  {event.source_event_url ? "Consulter l'événement sur le site source" : `Plus d'infos sur ${event.source_label}`}
                </div>
                <div className="text-sm text-slate-600 truncate">
                  Informations officielles et à jour{event.source_label ? ` · via ${event.source_label}` : ""}
                </div>
              </div>
              <span className="shrink-0 inline-flex items-center gap-1.5 h-11 px-5 rounded-xl bg-[#E05638] text-white font-bold group-hover:bg-[#c94a2f] transition-colors">
                <ExternalLink size={16}/> Ouvrir
              </span>
            </a>
          )}

          <div>
            <h2 className="font-heading text-xl font-bold">À propos</h2>
            <p className="mt-2 text-slate-700 whitespace-pre-line">{event.description || "Détails à venir."}</p>
            <SuggestLink />
            <BreadcrumbJsonLd items={[{ name: "Accueil", path: "/" }, { name: "Agenda", path: "/sortir" }, { name: event.title }]} />
          </div>
        </div>

        <aside className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3 h-fit" data-testid="event-sidebar">
          <div className="font-heading font-bold text-lg">Infos pratiques</div>
          <div className="flex gap-2 text-sm text-slate-700"><CalendarDays size={14} className="mt-0.5 shrink-0"/> {formatDateRange(event.start_date, event.end_date)}</div>
          {event.address && <div className="flex gap-2 text-sm text-slate-700"><MapPin size={14} className="mt-0.5 shrink-0"/> {event.address}, {event.city}</div>}
          {!event.address && <div className="flex gap-2 text-sm text-slate-700"><MapPin size={14} className="mt-0.5 shrink-0"/> {event.city}</div>}
          {event.organizer && <div className="text-sm text-slate-600"><b>Organisateur :</b> {event.organizer}</div>}
          {event.source_label && (
            <div className="text-sm text-slate-600 pt-2 border-t border-slate-100 flex items-center gap-1.5 flex-wrap" data-testid="event-source-name">
              <b>Source :</b>
              {event.source_logo && (
                <img src={event.source_logo} alt={event.source_label}
                     className="w-4 h-4 rounded-sm object-contain"
                     onError={(e) => {
                       if (event.source_logo_fallback && e.currentTarget.src !== event.source_logo_fallback) e.currentTarget.src = event.source_logo_fallback;
                       else e.currentTarget.style.display = "none";
                     }} />
              )}
              {event.source_label}
              {(event.source_event_url || event.source_site) && (
                <a href={event.source_event_url || event.source_site} target="_blank" rel="noreferrer"
                   className="ml-1 inline-flex items-center gap-1 font-semibold text-[#E05638] hover:underline">
                  <ExternalLink size={12}/> voir
                </a>
              )}
            </div>
          )}
        </aside>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 space-y-6">
        <FeaturedPartners limit={6} testId="event-detail-featured-partners" city={event.city || ""} />
        <EventBrandMentions />
      </div>

      <FbGroupShare
        text={`À ne pas manquer : ${event.title}${event.city ? " — " + event.city : ""}`}
        shareUrl={ogUrl}
        cityHint={event.city || ""}
      />
    </div>
  );
}
